import { useState } from 'react';

import { notify } from '../../shared';
import ExportModal from './ExportModal';
import ImportModal from './ImportModal';
import TransferModal from './TransferModal';
import CampaignSecretModal from './CampaignSecretModal';
import { useActions } from '../../molecules';
import { getErrorMessage } from '../../utilities';
import { useModal, useRequest } from '../../hooks';
import { useConfirm, useView } from '../../molecules';
import { addCampaign, updateCampaign, deleteCampaign } from '../../api';
import { copyCampaign, transferCampaignToOrganization } from '../../api';
import type { CampaignType } from '../../api';
import type { TransferCampaignToOrganizationPayload } from '../../api';
import type { CampaignCreatePayload, CampaignUpdatePayload } from '../../api';

export const useCampaignActions = (onRefresh: () => void) => {
  const baseActions = useActions<
    CampaignType,
    CampaignCreatePayload,
    CampaignUpdatePayload
  >({
    name: 'Campaign',

    onAdd: addCampaign,
    onEdit: updateCampaign,
    onDelete: deleteCampaign,
    onRefresh,
  });

  const { element: viewElement, handleView } = useView();

  const { element: copyElement, open: handleOpenCopy } = useConfirm();
  const [handleCopyCampaign, copying] = useRequest(
    (_signal, item: ItemType) => {
      return copyCampaign(item.id);
    },
    {
      deps: [],
      onSuccess: () => {
        notify.success('Campaign copied successfully.');
        onRefresh();
      },
      onError: ex => {
        notify.error(getErrorMessage(ex, `Failed to copy!`));
      },
    }
  );

  const [selectedCampaignId, setSelectedCampaignId] = useState<string>();
  const { isOpened, onClose, onOpen } = useModal();
  const handleStartTransfer = (item: ItemType) => {
    setSelectedCampaignId(item.id);
    onOpen();
  };
  const [selectedSecretCampaignId, setSelectedSecretCampaignId] =
    useState<string>();
  const {
    isOpened: isSecretModalOpened,
    onClose: onCloseSecretModal,
    onOpen: onOpenSecretModal,
  } = useModal();
  const handleStartSecrets = (item: ItemType) => {
    setSelectedSecretCampaignId(item.id);
    onOpenSecretModal();
  };

  const secretElement =
    isSecretModalOpened && !!selectedSecretCampaignId ? (
      <CampaignSecretModal
        campaignId={selectedSecretCampaignId}
        onClose={onCloseSecretModal}
      />
    ) : null;

  const [handleTransferCampaign, transfering] = useRequest(
    (_signal, payload: TransferCampaignToOrganizationPayload) => {
      return transferCampaignToOrganization(payload);
    },
    {
      deps: [],
      onSuccess: () => {
        notify.success('Campaign transferred successfully.');
        onRefresh();
        onClose();
      },
      onError: ex => {
        notify.error(getErrorMessage(ex, `Failed to transfer!`));
      },
    }
  );

  const transferElement = isOpened && !!selectedCampaignId && (
    <TransferModal
      onClose={onClose}
      loading={transfering}
      campaignId={selectedCampaignId}
      onSumbit={handleTransferCampaign}
    />
  );

  const [selectedExportCampaignId, setSelectedExportCampaignId] =
    useState<string>();
  const {
    isOpened: isExportModalOpened,
    onClose: onCloseExportModal,
    onOpen: onOpenExportModal,
  } = useModal();
  const handleStartExport = (item: ItemType) => {
    setSelectedExportCampaignId(item.id);
    onOpenExportModal();
  };

  const exportElement =
    isExportModalOpened && !!selectedExportCampaignId ? (
      <ExportModal
        campaignId={selectedExportCampaignId}
        onClose={onCloseExportModal}
      />
    ) : null;

  const {
    isOpened: isImportModalOpened,
    onClose: onCloseImportModal,
    onOpen: onOpenImportModal,
  } = useModal();

  const importElement = isImportModalOpened ? (
    <ImportModal onClose={onCloseImportModal} onSuccess={onRefresh} />
  ) : null;

  return {
    viewElement,
    handleView,

    secretElement,
    handleStartSecrets,

    transfering,
    transferElement,
    handleStartTransfer,

    exportElement,
    handleStartExport,

    importElement,
    handleStartImport: onOpenImportModal,

    copying,
    copyElement,
    handleStartCopy: (item: ItemType) => {
      return handleOpenCopy({
        title: 'Copy Campaign',
        confirmText: 'Copy',
        onConfirm: () => handleCopyCampaign(item),
        text: `Are you sure you want to copy ${item.name}?`,
      });
    },

    ...baseActions,
  };
};

export type ItemType = CampaignType;
export type ApiPayload = CampaignCreatePayload;

export type FormType = Omit<
  Partial<CampaignCreatePayload>,
  'strategy' | 'dialer_retry_backoff_minutes' | 'dialer_calling_schedule'
> & {
  export_fields?: string;
  max_slot_capacity?: string;
  max_concurrent_calls?: string;
  dialer_calling_schedule?: string;
  time_slot_duration_minutes?: string;
  dialer_retry_backoff_minutes?: string;
  monitoring_fields?: DynamicColumnType[];
};
